import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { actesDocuments, actesDocumentLink, actesFileLink, type ActeDoc } from '@/integrations/actesApi';
import { DocViewer } from './DocViewer';
import { actesEn } from '@/lib/actesCountry';

/**
 * WHICH DOCUMENT? — when a demande carries more than one.
 *
 * One document opens straight away; there is no decision to make. Two or more
 * and the row cannot guess, so it asks: the received act at the top, then
 * everything attached to it, each saying what it is. Same as the old site did.
 */
const CAT: Record<string, { fr: string; en: string; color: string }> = {
  recu: { fr: 'Reçu', en: 'Received', color: 'emerald' },
  ancienne: { fr: 'Ancienne copie', en: 'Older copy', color: 'amber' },
  premiere_demande: { fr: 'Première demande', en: 'First application', color: 'sky' },
  lie: { fr: 'Acte lié', en: 'Related record', color: 'violet' },
  autre: { fr: 'Autre', en: 'Other', color: 'slate' },
};

export function DocumentPicker({ lang, acteId, person, hasReceived, receivedName, onClose }: {
  lang: Lang; acteId: string; person?: string;
  hasReceived: boolean; receivedName?: string | null; onClose: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const [docs, setDocs] = useState<ActeDoc[] | null>(null);
  const [err, setErr] = useState('');
  /* Opened HERE, over the list, so the choice is still behind it. */
  const [view, setView] = useState<(() => Promise<{ url: string; name: string | null; mime?: string | null }>) | null>(null);

  useEffect(() => {
    let live = true;
    actesDocuments(acteId).then((d) => { if (live) setDocs(d); }).catch(() => { if (live) setDocs([]); });
    return () => { live = false; };
  }, [acteId]);

  const open = (fn: () => Promise<{ url: string; name: string | null; mime?: string | null }>) => {
    setErr('');
    setView(() => fn);
  };

  return createPortal(
    <div className="svc-modal-backdrop actes-skin" onClick={onClose}>
      <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <button className="svc-modal-close" onClick={onClose}>×</button>
        <div className="svc-modal-body">
          <div className="svc-modal-tag">{T('Documents', 'Documents')}</div>
          <h2>{person || T('Quel document ?', 'Which document?')}</h2>

          <ul className="actes-docs-list">
            {hasReceived && (
              <li>
                <KBIcon name="documents" size={14} />
                <span className="actes-docs-n">{receivedName || T('Acte reçu', 'Received act')}</span>
                <span className="actes-tag c-emerald">{T('Reçu', 'Received')}</span>
                <button className="actes-row-act" title={T('Voir', 'View')}
                  onClick={() => open(() => actesFileLink(acteId))}>
                  <KBIcon name="eye" size={13} />
                </button>
              </li>
            )}
            {docs === null
              ? <li><span className="muted-note">{T('Chargement…', 'Loading…')}</span></li>
              : docs.map((d) => {
                const c = CAT[d.category] ?? CAT.autre;
                return (
                  <li key={d.id}>
                    <KBIcon name="documents" size={14} />
                    <span className="actes-docs-n" title={d.file_name ?? ''}>{d.file_name || T('Document', 'Document')}</span>
                    <span className={`actes-tag c-${c.color}`}>{T(c.fr, c.en)}</span>
                    <button className="actes-row-act" title={T('Voir', 'View')}
                      onClick={() => open(() => actesDocumentLink(acteId, d.id))}>
                      <KBIcon name="eye" size={13} />
                    </button>
                  </li>
                );
              })}
          </ul>

          {err && <p className="muted-note ni-err">⚠ {err}</p>}

          <div className="nt-actions">
            <button className="btn btn-ghost" onClick={onClose}>{T('Fermer', 'Close')}</button>
          </div>
        </div>
      </div>
      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}
    </div>,
    document.body,
  );
}

/** The button that leads the row: the file, its count, and what a click does. */
export function DocCell({ lang, total, onOpen }: { lang: Lang; total: number; onOpen: () => void }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  if (total === 0) return <span className="actes-doccell empty" />;
  return (
    <button className="actes-doccell" title={T('Voir le document', 'View the document')}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}>
      <KBIcon name="documents" size={14} />
      {total > 1 && <em>{total}</em>}
    </button>
  );
}
